/**
 * V103-10-6 — pure helpers for the government-cloud move screens (ADR-0069 +
 * owner decisions): live notice dates from the rules, the target scope from
 * the per-component choices, the gap grouped for reading, and plan labels.
 * No I/O; unit-tested in __tests__/transition-model.test.ts.
 */
import { addTimeframe, requirementLabel, subtractTimeframe, type GapObjectiveRow, type PlanItemView, type ProjectedMilestone, type TransitionGap } from '@aigovops/core';
import type { NoticeWindow } from './boundary-api';
import { programLabel } from './boundary-model';

type Unit = Parameters<typeof addTimeframe>[2];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const day = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00.000Z`);
/** "Fri, Dec 15, 2026" (UTC; the rules count UTC business days). */
export const longDate = (d: Date) => `${DAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
/** "Dec 15" (or with the year when it differs from `ref`). */
export function shortDate(iso: string | null | undefined, ref: Date = new Date()): string {
  if (!iso) return '';
  const d = day(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}${d.getUTCFullYear() !== ref.getUTCFullYear() ? `, ${d.getUTCFullYear()}` : ''}`;
}
const unitText = (n: number, u: string) => (u === 'bizdays' ? `${n} business days` : u === 'days' ? `${n} days` : `${n} ${u}`);

export interface NoticeDue {
  program: string;
  label: string;
  /** Null: the rule has no approved window (GovRAMP: confirm with your assessor). */
  due: Date | null;
  rule: string;
  tooSoon: boolean;
  earliest: Date | null;
}

/** "FedRAMP notice due by …" for a planned cutover day, per program, from the approved rules. */
export function noticeDue(cutoverDay: string, windows: ReadonlyArray<NoticeWindow>, today: Date = new Date()): NoticeDue[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(cutoverDay)) return [];
  const cut = day(cutoverDay);
  const t0 = day(today.toISOString());
  return windows.map((w) => {
    const label = programLabel(w.program);
    if (w.timeframe_num === null || !w.timeframe_type) return { program: w.program, label, due: null, rule: 'Confirm the timing with your assessor', tooSoon: false, earliest: null };
    const due = subtractTimeframe(cut, w.timeframe_num, w.timeframe_type as Unit);
    const tooSoon = due.getTime() < t0.getTime();
    return {
      program: w.program, label, due, rule: `${unitText(w.timeframe_num, w.timeframe_type)} before`, tooSoon,
      earliest: tooSoon ? addTimeframe(t0, w.timeframe_num, w.timeframe_type as Unit) : null,
    };
  });
}

/** A target register row's key: the current key plus the offering ("orders-db-aws-govcloud"). */
export function targetKey(componentKey: string, offeringKey: string): string {
  return `${componentKey}-${offeringKey}`.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').slice(0, 200);
}

export type MoveChoice = { mode: 'move'; target_component_id: string } | { mode: 'drop' } | { mode: 'keep' };

/** The FULL target list for one program from its standing components and the per-component choices (default: keep). */
export function targetScope(standing: ReadonlyArray<string>, choices: ReadonlyMap<string, MoveChoice>): Array<{ component_id: string; replaces_component_id?: string | null }> {
  const out: Array<{ component_id: string; replaces_component_id?: string | null }> = [];
  const used = new Set<string>();
  for (const id of standing) {
    const c = choices.get(id) ?? { mode: 'keep' };
    if (c.mode === 'drop') continue;
    if (c.mode === 'move' && !used.has(c.target_component_id)) { out.push({ component_id: c.target_component_id, replaces_component_id: id }); used.add(c.target_component_id); continue; }
    if (!used.has(id)) { out.push({ component_id: id }); used.add(id); }
  }
  return out;
}

/** The choices a saved target scope encodes (round-trip of `targetScope`). */
export function choicesFrom(standing: ReadonlyArray<string>, target: ReadonlyArray<{ component_id: string; replaces_component_id: string | null }>): Map<string, MoveChoice> {
  const m = new Map<string, MoveChoice>();
  const kept = new Set(target.filter((t) => !t.replaces_component_id).map((t) => t.component_id));
  for (const id of standing) {
    const r = target.find((t) => t.replaces_component_id === id);
    m.set(id, r ? { mode: 'move', target_component_id: r.component_id } : kept.has(id) ? { mode: 'keep' } : { mode: 'drop' });
  }
  return m;
}

export interface ObjectiveGroup {
  requirement_id: string;
  label: string;
  change: GapObjectiveRow['change'];
  rows: GapObjectiveRow[];
  proposed: number;
  confirmed: number;
  unverified: boolean;
}
/** Objectives that move, grouped per requirement × change (8 PE objectives read as one row). */
export function groupObjectives(gap: Pick<TransitionGap, 'objectives'>): ObjectiveGroup[] {
  const by = new Map<string, ObjectiveGroup>();
  for (const o of gap.objectives) {
    if (o.change === 'unchanged') continue;
    const k = `${o.change}|${o.requirement_id}`;
    const g = by.get(k) ?? { requirement_id: o.requirement_id, label: requirementLabel(o.requirement_id), change: o.change, rows: [], proposed: 0, confirmed: 0, unverified: false };
    g.rows.push(o);
    if (o.proposal?.status === 'proposed') g.proposed++;
    if (o.proposal?.status === 'confirmed') g.confirmed++;
    if (o.proposal?.verified === false) g.unverified = true;
    by.set(k, g);
  }
  const order: Record<string, number> = { gained: 0, provider_changed: 1, party_changed: 2, lost: 3 };
  return [...by.values()].sort((a, b) => (order[a.change] ?? 9) - (order[b.change] ?? 9) || (a.label < b.label ? -1 : 1));
}

/** Three or more controls of one 800-53 family with the same change read as one row ("PE family · 8 controls"). */
export function collapseFamilies(groups: ObjectiveGroup[]): Array<ObjectiveGroup & { members: string[] }> {
  const out: Array<ObjectiveGroup & { members: string[] }> = [];
  const fam = (g: ObjectiveGroup) => (/^[A-Z]{2}-\d/.test(g.label) ? `${g.change}|${g.label.slice(0, 2)}` : null);
  const counts = new Map<string, number>();
  for (const g of groups) { const f = fam(g); if (f) counts.set(f, (counts.get(f) ?? 0) + 1); }
  const merged = new Map<string, ObjectiveGroup & { members: string[] }>();
  for (const g of groups) {
    const f = fam(g);
    if (!f || (counts.get(f) ?? 0) < 3) { out.push({ ...g, members: [g.label] }); continue; }
    const m = merged.get(f);
    if (m) { m.rows.push(...g.rows); m.proposed += g.proposed; m.confirmed += g.confirmed; m.unverified ||= g.unverified; m.members.push(g.label); continue; }
    const first = { ...g, rows: [...g.rows], label: `${g.label.slice(0, 2)} family`, members: [g.label] };
    merged.set(f, first); out.push(first);
  }
  for (const m of merged.values()) { m.members.sort((a, b) => a.localeCompare(b, 'en', { numeric: true })); m.label = `${m.label} · ${m.members.length} controls`; }
  return out;
}

export const CHANGE_HEADING: Record<string, string> = {
  gained: 'Gained · become provider-held', provider_changed: 'Basis changes', party_changed: 'Responsibility changes', lost: 'Lost · you hold them after the move',
};

/** Proposals still to review in a gap (they are not counted until a person confirms them). */
export const openProposals = (gap: Pick<TransitionGap, 'objectives'>) => gap.objectives.filter((o) => o.proposal?.status === 'proposed').length;

/** Plan status (state, not action). */
export function planStatus(i: Pick<PlanItemView, 'work_item_status' | 'window_status' | 'scheduled_for'>): { text: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' } {
  if (i.work_item_status === 'done' || i.work_item_status === 'resolved') return { text: 'Done', tone: 'ok' };
  if (i.work_item_status === 'cancelled') return { text: 'Cancelled', tone: 'neutral' };
  if (i.window_status === 'confirm_with_assessor') return { text: 'Timing not confirmed', tone: 'warn' };
  if (i.window_status === 'blocked_rules_not_approved') return { text: 'No date · rule not approved', tone: 'warn' };
  if (i.window_status === 'after_cutover') return { text: 'After cutover', tone: 'neutral' };
  return { text: 'Not started', tone: 'neutral' };
}

/** "Already planned · Dec 4" / "Not needed" / "New at plan" — the projected milestone's state. */
export function projectedLabel(m: Pick<ProjectedMilestone, 'status' | 'due_at'>): string {
  if (m.status === 'already_planned') return `Already planned${m.due_at ? ` · ${shortDate(m.due_at)}` : ''}`;
  if (m.status === 'planned') return `Planned${m.due_at ? ` · ${shortDate(m.due_at)}` : ''}`;
  if (m.status === 'not_needed') return 'Not needed';
  return 'Added when you build the plan';
}

/** Viewed program tabs, per transition (sessionStorage; the "1 of 2 viewed" gate before the plan). */
export function readViewed(transitionId: string): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(`gp-tr-viewed:${transitionId}`) ?? '[]') as string[]); } catch { return new Set(); }
}
export function markViewed(transitionId: string, program: string): Set<string> {
  const s = readViewed(transitionId);
  s.add(program);
  try { sessionStorage.setItem(`gp-tr-viewed:${transitionId}`, JSON.stringify([...s])); } catch { /* private mode: in-memory only */ }
  return s;
}
